import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { TypedMemo } from '../../../../common/types';
import { ErrorAPIResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { StudioActionError } from '../StudioActionError';
import { useStudioActionFeedback } from '../useStudioActionFeedback';
import { ReelFailReason } from '../../types';
import { reelFailAction } from './reelStages';
import { reelFailMessages, reelProgressMessages } from './reelProgressMessages';
import { aiBudgetMessages } from '../../aiBudgetMessages';
import { useUpgradePrompt } from '../../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../../config/music24PlanCapabilities';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole AI reel flow; each part uses its own slice of it */
import styles from './PromoteReels.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelFailNoticeProps {
  failReason: ReelFailReason | null;
  isRetrying: boolean;
  /** The error of a failed "Try again" (a 5xx or a network error). The button tries again. */
  tryAgainError?: ErrorAPIResult['error'];
  /** "Try again": a new reel with the same form values. */
  onTryAgain: () => void;
  /** "Change the song": the form with the song field empty. */
  onChangeSong: () => void;
  /** "Change the settings" and "Back to the Studio": the form with its values. */
  onChangeSettings: () => void;
}

/**
 * The failed reel (design b3, b4): one sentence per reason, the refund line, the main button for
 * the reason and "Change the settings". A spent Free AI budget (`AI_BUDGET_EXHAUSTED`) offers the
 * upgrade: a new try fails the same way until the plan changes or the month ends.
 */
const ReelFailNoticeComponent: React.FC<ReelFailNoticeProps> = (props) => {
  const { failReason, isRetrying, tryAgainError, onTryAgain, onChangeSong, onChangeSettings } = props;
  const intl = useIntl();
  const action = reelFailAction(failReason);
  const openUpgrade = useUpgradePrompt();
  const upgrade = useCallback(() => openUpgrade(SMLUpgradeFeature.aiPriority), [openUpgrade]);
  // A failed "Try again" shows under the buttons, with the general sentence for a raw error.
  const tryAgainFeedback = useStudioActionFeedback('try the AI reel again', isRetrying ? undefined : tryAgainError);

  let main: React.ReactNode;
  if (action === 'changeSong') {
    main = (
      <SMLButton color="primary" omitCap onClick={onChangeSong}>
        {intl.formatMessage(reelProgressMessages.changeSong)}
      </SMLButton>
    );
  } else if (action === 'upgrade') {
    main = (
      <SMLButton color="primary" omitCap onClick={upgrade}>
        {intl.formatMessage(aiBudgetMessages.upgrade)}
      </SMLButton>
    );
  } else if (action === 'backToStudio') {
    main = (
      <SMLButton color="primary" omitCap onClick={onChangeSettings}>
        {intl.formatMessage(reelProgressMessages.backToStudio)}
      </SMLButton>
    );
  } else {
    main = (
      <SMLButton color="primary" omitCap isLoading={isRetrying} disabled={isRetrying} onClick={onTryAgain}>
        {intl.formatMessage(reelProgressMessages.tryAgain)}
      </SMLButton>
    );
  }

  return (
    <>
      <Alert color="danger" className="mb-0" role="alert">
        <strong>{intl.formatMessage(reelFailMessages[failReason ?? 'unknown'])}</strong>
        <br />
        {intl.formatMessage(reelProgressMessages.refundLine)}
      </Alert>
      <div className={styles.actions}>
        {main}
        {action !== 'backToStudio' && (
          <SMLButton outline omitCap onClick={onChangeSettings}>
            {intl.formatMessage(reelProgressMessages.changeSettings)}
          </SMLButton>
        )}
      </div>
      <StudioActionError feedback={tryAgainFeedback} />
    </>
  );
};

export const ReelFailNotice = TypedMemo(ReelFailNoticeComponent);
